/**
 * Luật thuần của module eKYC — không gọi API, không chạm React.
 *
 * Mọi hàm ở đây giữ một bất biến mà bộ bàn giao nhắc lại ở cả năm tệp: **"không đo được" khác
 * "đo ra 0"** (`00` §4.2, `04` §13.4, `05` §8.2). Đó không phải một chi tiết hiển thị — đọc sai
 * chỗ này là **từ chối oan một người thật**, và màn hình sẽ trông hoàn toàn bình thường khi làm vậy.
 */
import type { KetQuaHangMuc, PhienTomTat, TabHoSo, TrangThaiPhien } from '../types';

/**
 * Trạng thái thuộc về từng tab của màn Hồ sơ định danh.
 *
 * `roi` gom `EXPIRED` và `CANCELLED` nhưng **không** gom `REJECTED` (`08` §6): "người dùng bỏ dở"
 * và "hệ thống từ chối" khác hẳn nhau khi đo tỉ lệ rơi, và trộn hai thứ làm mất đúng con số mà
 * người vận hành cần. `ERROR` cũng vào `roi` vì nó là **luồng hỏng**, không phải một quyết định
 * về một con người.
 */
const TRANG_THAI_THEO_TAB: Record<Exclude<TabHoSo, 'tat-ca'>, readonly TrangThaiPhien[]> = {
  'cho-nguoi': ['MANUAL_REVIEW', 'ADDITIONAL_REQUIRED'],
  'dang-chay': [
    'CREATED',
    'CONSENT_REQUIRED',
    'DOCUMENT_REQUIRED',
    'OCR_PROCESSING',
    'FRAUD_CHECKING',
    'INFO_CONFIRMATION',
    'SELFIE_REQUIRED',
    'LIVENESS_PROCESSING',
    'VERIFICATION_PROCESSING',
  ],
  dat: ['VERIFIED'],
  'tu-choi': ['REJECTED'],
  roi: ['EXPIRED', 'CANCELLED', 'ERROR'],
};

/** Trạng thái mà tab đang chọn chấp nhận; `tat-ca` trả `undefined` (không lọc). */
export function trangThaiCuaTab(tab: TabHoSo): readonly TrangThaiPhien[] | undefined {
  return tab === 'tat-ca' ? undefined : TRANG_THAI_THEO_TAB[tab];
}

/**
 * Phiên đang chạy thì màn tự hỏi lại server.
 *
 * Dùng để bật `refetchInterval`: hết phiên đang chạy thì **dừng hỏi**. Hỏi vòng vĩnh viễn trên một
 * danh sách đã đứng yên chỉ đốt pin của người dùng và đốt hạn mức của server.
 */
export function dangChay(trangThai: TrangThaiPhien): boolean {
  return TRANG_THAI_THEO_TAB['dang-chay'].includes(trangThai);
}

export function coPhienDangChay(dong: readonly PhienTomTat[]): boolean {
  return dong.some((p) => dangChay(p.status));
}

/**
 * Hồ sơ còn huỷ được không — khớp bảng chuyển `CANCELLED` của máy chủ (`ho-so/trang-thai.ts`).
 *
 * `MANUAL_REVIEW` **không** huỷ được: máy đã xong việc, hồ sơ đang chờ một con người quyết. Muốn bỏ thì người thẩm
 * định từ chối, để lại lý do — huỷ ngang là xoá mất câu trả lời "vì sao hồ sơ này không được duyệt".
 */
export function huyDuoc(trangThai: TrangThaiPhien): boolean {
  return dangChay(trangThai) || trangThai === 'ADDITIONAL_REQUIRED';
}

/**
 * Hồ sơ có xoá dữ liệu cá nhân được không — khớp bảng chuyển `DELETED` của máy chủ.
 *
 * Chỉ hồ sơ **đã chốt**. Hồ sơ đang chạy thì phải huỷ trước: xoá ảnh giữa lúc máy đang đọc chúng là để lại một phiên
 * nửa vời mà không ai giải thích được.
 */
export function xoaDuoc(trangThai: TrangThaiPhien): boolean {
  return ['MANUAL_REVIEW', 'VERIFIED', 'REJECTED', 'EXPIRED', 'CANCELLED', 'ERROR'].includes(trangThai);
}

/**
 * Hạng mục có cho ra một con số đọc được không.
 *
 * `SKIPPED` và `ERROR` **đều mang `score = 0.0`** (`04` §1.3). Vẽ thanh điểm theo `score` mà bỏ qua
 * `result` sẽ hiện "0 điểm" cho một hạng mục **chưa từng chạy** — người duyệt đọc thành "đã kiểm
 * và trượt sạch", rồi từ chối một hồ sơ mà **chưa có phép kiểm nào chạy trên nó**.
 */
export function coDiem(ket: KetQuaHangMuc): boolean {
  return ket.result !== 'SKIPPED' && ket.result !== 'ERROR';
}

/**
 * Khoảng cách tới ngưỡng, dương là đã vượt.
 *
 * Trả `null` khi không đo được **hoặc** hạng mục không có ngưỡng — cả hai đều là "không có câu trả
 * lời", và ép thành một con số nào đó là bịa ra một kết luận.
 */
export function khoangCachNguong(ket: KetQuaHangMuc): number | null {
  if (!coDiem(ket) || ket.threshold === null) return null;
  return ket.score - ket.threshold;
}

/**
 * Phiên này có cần banner "chưa vào sổ định danh" không.
 *
 * Chỉ hỏi trên phiên **đã duyệt**: một hồ sơ bị từ chối đương nhiên không vào sổ, và báo điều đó
 * là nhiễu. Trên phiên `VERIFIED` thì `registryEnrolled === false` nghĩa là người đó **đăng ký lại
 * được** — đã xảy ra thật trên production (`07` §23.1).
 */
export function thieuGhiSo(trangThai: TrangThaiPhien, daGhiSo: boolean): boolean {
  return trangThai === 'VERIFIED' && !daGhiSo;
}

/**
 * Quyết định có nên hiện thẻ triển khai dưới dạng cảnh báo hay không.
 *
 * `enforcedBy: 'gateway'` mà `selfEnforced: false` ở một bản **không có gateway phía trước** nghĩa
 * là **mọi JWT hợp lệ đều toàn quyền**: đọc `GET /sessions` của mọi đối tác, xem ảnh mặt và số
 * giấy tờ người thật (`00` §9). Chỗ duy nhất phát hiện ra là một dòng log lúc khởi động và thẻ này.
 */
export function ruiRoPhanQuyen(enforcedBy: 'gateway' | 'tu_kiem', selfEnforced: boolean): boolean {
  return enforcedBy === 'gateway' && !selfEnforced;
}

/**
 * Vùng ảnh và dữ liệu cá nhân ở màn thẩm định có hiện không.
 *
 * Tách ra khỏi view thành hàm thuần vì chuỗi cấp quyền của nó đi qua **bốn** chỗ, và đứt ở bất kỳ
 * chỗ nào cũng cho ra cùng một triệu chứng câm lặng — màn vẫn mở, chỉ là không ai xem được ảnh:
 *
 * 1. `EKYC_REVIEW` phải có trong `TIEN_TO_PHAN_HE` (`lib/route-permissions.ts`), nếu không
 *    `tinhQuyenCongTenant` không bao giờ sinh ra mã này cho tài khoản thật;
 * 2. dịch vụ `dinh-danh-ekyc` phải nằm trong gói của tenant;
 * 3. tài khoản mẫu phải khai mã này trong `mock/fixtures.ts`;
 * 4. `QUYEN.thamDinhDoc` phải khớp **chính xác** chuỗi mã — `coMaQuyen` không có ký tự đại diện.
 *
 * Một hàm thuần cho phép test đi qua cả bốn mà không cần dựng React.
 *
 * ⚠️ Mã ở đây phải khớp `QUYEN.thamDinhDoc` của `constants.ts` — view hỏi qua
 * `useModulePermissions(QUYEN)`, hàm này hỏi trực tiếp. Hai nơi lệch nhau thì test vẫn xanh trong
 * khi màn thật ẩn mất vùng ảnh, nên `ekyc.test.ts` canh đúng cặp đó bằng một ca riêng.
 */
export const MA_QUYEN_XEM_PII = 'EKYC_REVIEW.READ';

export function xemDuocPii(quyen: readonly string[], toanQuyen = false): boolean {
  return toanQuyen || quyen.includes(MA_QUYEN_XEM_PII);
}
